import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function RentersLiabilityArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'How Much Liability Do I Need for Renters Insurance? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn how much renters insurance liability you may need, compare $100K, $300K, and $500K limits, and choose a limit that fits your rental situation.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="container">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '260px 1fr',
          gap: '2rem',
        }}
      >
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />

        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/knowledge-base')}
              style={{ marginBottom: '1rem' }}
            >
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>How much liability do I need for renters insurance?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A practical breakdown of how to choose between $100,000, $300,000, and $500,000 liability limits for
              your renters policy. If you are new to policy basics, start with{' '}
              <Link to="/knowledge-base/what-is-ho4-renters-insurance" style={articleLinkStyle}>
                what HO-4 renters insurance is
              </Link>
              .
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>
              Last modified: {HO4_LAST_MODIFIED}
            </p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Many renters choose at least <strong>$300,000</strong> in personal liability coverage because lawsuits
                and medical costs can exceed lower limits quickly. A <strong>$100,000</strong> limit may be enough in
                some low-risk situations, while <strong>$500,000</strong> can make sense if you have more exposure.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What liability coverage helps pay for</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Injuries to guests in your unit, such as slips, falls, or other accidental injuries.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Damage you cause to someone else&apos;s property, like a kitchen fire spreading to another unit.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Legal defense costs if a covered incident leads to a claim or lawsuit.
                </li>
                <li>Some policies include limited no-fault medical payments for guests.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>
                Comparing common liability limits
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Limit
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Best for
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Consideration
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$100,000</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Budget-first renters with low exposure
                      </td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Can be exhausted quickly in severe claims
                      </td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$300,000</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Most renters and first-time policyholders
                      </td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>
                        Better cushion at a moderate premium increase
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>$500,000</td>
                      <td style={{ padding: '0.6rem' }}>
                        Higher-risk or higher-asset households
                      </td>
                      <td style={{ padding: '0.6rem' }}>
                        Stronger protection when claim severity is a concern
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                Need help getting a policy first? Follow this step-by-step guide on{' '}
                <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>
                  how to get HO-4 renters insurance
                </Link>
                .
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How to choose in 5 minutes</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Start with your landlord&apos;s requirement if your lease has a minimum liability limit.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Think through your risk profile: pets, frequent guests, parties, or shared living spaces.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Compare the monthly premium difference between $100K, $300K, and $500K.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  If the step-up cost is small, many renters select the next higher tier for margin.
                </li>
                <li>Review annually as your living setup changes.</li>
              </ol>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common mistakes to avoid</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Choosing only by lowest premium without checking liability limits.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>Assuming the landlord&apos;s policy covers your liability.</li>
                <li>
                  Forgetting to update limits after life changes, such as a new pet or roommate. You can also review{' '}
                  <Link
                    to="/knowledge-base/how-is-ho4-renters-insurance-different-from-homeowners-insurance"
                    style={articleLinkStyle}
                  >
                    how HO-4 differs from homeowners insurance
                  </Link>{' '}
                  to avoid coverage assumptions.
                </li>
              </ul>
            </div>
          </article>

          <section
            className="card mb-4"
            style={{
              borderColor: 'var(--color-green)',
              background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>
                Start renters insurance with GetSkimp
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                Compare coverage options and choose a liability limit that fits your rental situation.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/qualify')}
                style={{ fontWeight: 700, paddingInline: '1.4rem' }}
              >
                Get started now
              </button>
            </div>
          </section>

          <section
            className="card"
            style={{
              borderColor: '#FBBF24',
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Want to confirm your limit with an agent?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Get one-on-one help choosing the right liability amount for your lease and risk profile.
              </p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/help')}
                style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}
              >
                Speak to an agent
              </button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default RentersLiabilityArticlePage;
